import { useQuery } from '@nerdwallet/apollo';
import { CenteredContainer } from '@nerdwallet/currency';
import defaultsDeep from 'lodash/defaultsDeep';
import { usePathname } from 'next/navigation';
import { Fragment, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isInBuilder } from '~/app/lib/helpers';
import { useIdentity } from '~/app/lib/redux/selectors/identity-selector';
import { useMediaQuery } from '~/app/lib/use-media-query';
import { marketplaceOffers } from '../keto-product-card/queries';
import { Component } from './dynamic-grid-component';
import { Container } from './dynamic-grid-container';
import { DynamicGridContext } from './dynamic-grid-context';
import { dynamicGridEditingConfigs } from './dynamic-grid-editing-configs';
import {
  isValidUUID,
  setDisableGridOutline,
  setMarketplaceOfferIds,
} from './dynamic-grid-helpers';
import { ModalContainer } from './dynamic-grid-modal';
import { getStickyGridStyle, useStuck } from './use-stuck';
import { flattenFormProperties, flattenIndexedValues } from './utils';

const Wrapper = ({
  children,
  isModal,
  isCenteredInParent,
  isStickyGridEnabled,
  modalVerticalAlignment,
  modalHorizontalAlignment,
  modalTimeoutValue,
  modalId,
  stickyContainerRef,
  stickyContainerStyle,
  centeredInParentBackgroundColor,
  centeredInParentBackgroundImageSize,
  centeredInParentBackgroundImage,
  modalShouldOpenOnMouseMove,
  modalShowCloseButton,
  modalCloseButtonProps,
  modalBorders,
  modalSpacing,
  isBuilderPage,
  centeredInParentBackgroundImagePositionX,
  centeredInParentBackgroundImagePositionY,
  centeredInParentBackgroundImageTablet,
  centeredInParentBackgroundImageMobile,
}: {
  children: React.ReactNode;
  isModal: boolean;
  isCenteredInParent: boolean;
  isStickyGridEnabled: boolean;
  modalVerticalAlignment: string;
  modalHorizontalAlignment: string;
  modalTimeoutValue?: number;
  modalId?: string;
  stickyContainerRef?: React.RefObject<HTMLDivElement>;
  stickyContainerStyle?: any;
  centeredInParentBackgroundColor: string;
  centeredInParentBackgroundImageSize: string;
  centeredInParentBackgroundImage: string;
  modalShouldOpenOnMouseMove?: boolean;
  modalShowCloseButton?: boolean;
  modalCloseButtonProps?: any;
  modalBorders?: any;
  modalSpacing?: any;
  isBuilderPage: boolean;
  centeredInParentBackgroundImagePositionX: string;
  centeredInParentBackgroundImagePositionY: string;
  centeredInParentBackgroundImageTablet: string;
  centeredInParentBackgroundImageMobile: string;
}) => {
  const size = useMediaQuery();

  const backgroundImage = useMemo(() => {
    const backgroundsImageMap = {
      lg: centeredInParentBackgroundImage,
      md:
        centeredInParentBackgroundImageTablet ||
        centeredInParentBackgroundImage,
      sm:
        centeredInParentBackgroundImageMobile ||
        centeredInParentBackgroundImageTablet ||
        centeredInParentBackgroundImage,
    };
    return (
      backgroundsImageMap[size as keyof typeof backgroundsImageMap] || undefined
    );
  }, [
    size,
    centeredInParentBackgroundImage,
    centeredInParentBackgroundImageTablet,
    centeredInParentBackgroundImageMobile,
  ]);

  if (isStickyGridEnabled) {
    return (
      <div style={stickyContainerStyle} ref={stickyContainerRef}>
        {children}
      </div>
    );
  }
  if (isModal) {
    if (isBuilderPage) {
      return <Fragment>{children}</Fragment>;
    }
    // Use the new flattenModalStyles function to handle indexed objects and nested structures
    const flattenedModalBorders = modalBorders
      ? flattenFormProperties(modalBorders)
      : undefined;
    const flattenedModalSpacing = modalSpacing
      ? flattenFormProperties(modalSpacing)
      : undefined;
    const flattenedCloseButtonProps = modalCloseButtonProps
      ? flattenFormProperties(modalCloseButtonProps)
      : undefined;

    return (
      <ModalContainer
        modalTimeoutValue={modalTimeoutValue}
        modalVerticalAlignment={modalVerticalAlignment}
        modalHorizontalAlignment={modalHorizontalAlignment}
        modalId={modalId}
        shouldOpenOnMouseMove={modalShouldOpenOnMouseMove}
        showCloseButton={modalShowCloseButton}
        modalBorders={flattenedModalBorders}
        modalSpacing={flattenedModalSpacing}
        closeButtonProps={flattenedCloseButtonProps}
      >
        {children}
      </ModalContainer>
    );
  }
  if (isCenteredInParent) {
    return (
      <div
        style={{
          backgroundRepeat: 'no-repeat',
          backgroundColor: centeredInParentBackgroundColor,
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundSize: centeredInParentBackgroundImageSize,
          backgroundPositionX: centeredInParentBackgroundImagePositionX,
          backgroundPositionY: centeredInParentBackgroundImagePositionY,
        }}
      >
        <CenteredContainer>{children}</CenteredContainer>
      </div>
    );
  }
  return <Fragment>{children}</Fragment>;
};

const renderComponent = (
  components: any,
  pathContext: { path: string },
  componentId: string,
  segment: any
) => {
  return components?.map((component: any, index: number) => {
    const shouldMergeSegment =
      componentId === segment?.componentId &&
      component?.segment?.enableInteractionTracking;

    // merge the segment with the component.segment,
    // if the component.segment is not defined, use the segment from the parent element
    if (shouldMergeSegment) {
      component.segment = defaultsDeep(segment, component.segment);
    }

    return (
      <Component
        key={component?.$unique_key || `component-${index}`}
        component={component}
        pathContext={{ path: pathContext.path, index }}
        componentId={componentId}
      />
    );
  });
};

let parentSegment: object = {};

const renderElement = (
  parentElement: any,
  pathContext: { path: string } = { path: 'elements' },
  componentId: string,
  segment?: unknown
) => {
  if (!Object.keys(parentSegment).length) {
    parentSegment = {
      ...flattenIndexedValues(parentElement?.tracking),
      componentId,
    };
  }

  if (!parentElement?.$unique_key) {
    return null;
  }

  return (
    <Container key={parentElement?.$unique_key} parentElement={parentElement}>
      {renderComponent(
        parentElement?.components,
        pathContext,
        componentId,
        parentSegment
      )}

      {parentElement?.elements?.map((childElement: any, index: number) => (
        <Fragment key={childElement?.$unique_key || `element-${index}`}>
          {renderElement(
            { ...childElement, componentId, index },
            {
              path: `${pathContext.path}.elements.${index}`,
            },
            componentId,
            segment
          )}
        </Fragment>
      ))}
    </Container>
  );
};

const DynamicGrid = ({
  elements,
  componentId,
  stickyGrid,
  options = {
    disableGridOutline: false,
    centeredInParent: false,
    marketplaceOfferIds: '',
    centeredInParentBackgroundColor: '',
    centeredInParentHexColor: '',
    centeredInParentBackgroundImage: '',
    centeredInParentBackgroundImageSize: 'cover',
    centeredInParentBackgroundImagePositionX: 'left',
    centeredInParentBackgroundImagePositionY: 'top',
    centeredInParentBackgroundImageTablet: '',
    centeredInParentBackgroundImageMobile: '',
    hideWhenUserIsSignedIn: false,
  },
  modal = {
    enabled: false,
    verticalAlignment: 'center',
    horizontalAlignment: 'center',
    timeoutValue: 3,
    id: '',
    shouldOpenOnMouseMove: false,
    showCloseButton: false,
    closeButtonProps: {},
    borders: {},
    spacing: {},
  },
}: {
  elements: any;
  componentId: string;
  stickyGrid: any;
  options: {
    disableGridOutline: boolean;
    centeredInParent: boolean;
    centeredInParentBackgroundColor: string;
    centeredInParentHexColor: string;
    centeredInParentBackgroundImage: string;
    centeredInParentBackgroundImageSize: string;
    centeredInParentBackgroundImagePositionX: string;
    centeredInParentBackgroundImagePositionY: string;
    centeredInParentBackgroundImageTablet: string;
    centeredInParentBackgroundImageMobile: string;
    marketplaceOfferIds: string;
    hideWhenUserIsSignedIn: boolean;
  };
  modal: {
    id?: string;
    enabled: boolean;
    verticalAlignment: string;
    horizontalAlignment: string;
    timeoutValue?: number;
    shouldOpenOnMouseMove?: boolean;
    showCloseButton?: boolean;
    closeButtonProps?: any;
    borders?: any;
    spacing?: any;
  };
}) => {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const isBuilderPage = isInBuilder(pathname);

  const {
    disableGridOutline,
    centeredInParent,
    centeredInParentBackgroundColor,
    centeredInParentHexColor,
    centeredInParentBackgroundImage,
    centeredInParentBackgroundImageSize,
    centeredInParentBackgroundImagePositionX,
    centeredInParentBackgroundImagePositionY,
    centeredInParentBackgroundImageTablet,
    centeredInParentBackgroundImageMobile,
    hideWhenUserIsSignedIn,
  } = options;

  const marketplaceOfferIds = useMemo(
    () => options?.marketplaceOfferIds?.split(',')?.filter(isValidUUID),
    [options?.marketplaceOfferIds]
  );

  // stickyGrid settings
  const stickySettings = useMemo(() => stickyGrid, [stickyGrid]);
  const isStickyGridEnabled = ['sticky', 'fixed'].includes(
    stickySettings?.position
  );
  const [stickyContainerRef, isStuck] = useStuck({
    topSpace: stickySettings?.margin || 96,
    bottomSpace: 0,
    offset: stickySettings?.position === 'fixed' ? 96 : 0,
    isFixedContainer: stickySettings?.position === 'fixed',
    linkedComponentId:
      stickySettings?.position === 'fixed'
        ? stickySettings?.linkedComponentId
        : undefined,
  });
  const stickyContainerStyle = getStickyGridStyle(
    stickySettings,
    isStickyGridEnabled,
    isStuck,
    centeredInParent,
    isBuilderPage
  );
  // stickyGrid settings

  const { data = {} } = useQuery(marketplaceOffers, {
    variables: { ids: marketplaceOfferIds },
    skip: !marketplaceOfferIds?.length,
    fetchPolicy: 'no-cache',
  });

  const { isSignedIn } = useIdentity();

  useEffect(() => {
    dispatch(setDisableGridOutline({ componentId, disableGridOutline }));
  }, [componentId, disableGridOutline, dispatch]);

  useEffect(() => {
    if (data?.marketplaceEntitiesV2) {
      dispatch(
        setMarketplaceOfferIds(componentId, data?.marketplaceEntitiesV2)
      );
    }
  }, [data, dispatch, componentId]);

  const getCenteredInParentBackgroundColor = () => {
    if (centeredInParentBackgroundColor === 'HEXCOLOR') {
      return centeredInParentHexColor;
    }
    return centeredInParentBackgroundColor;
  };

  if (hideWhenUserIsSignedIn && isSignedIn) {
    return null;
  }

  return (
    <DynamicGridContext.Provider value={componentId}>
      <Wrapper
        isStickyGridEnabled={isStickyGridEnabled}
        isModal={modal?.enabled}
        isCenteredInParent={centeredInParent}
        modalVerticalAlignment={modal?.verticalAlignment}
        modalHorizontalAlignment={modal?.horizontalAlignment}
        modalTimeoutValue={modal?.timeoutValue}
        modalId={modal?.id}
        modalShouldOpenOnMouseMove={modal?.shouldOpenOnMouseMove}
        modalShowCloseButton={modal?.showCloseButton}
        modalCloseButtonProps={modal?.closeButtonProps}
        modalBorders={modal?.borders}
        modalSpacing={modal?.spacing}
        stickyContainerRef={
          isStickyGridEnabled ? stickyContainerRef : undefined
        }
        stickyContainerStyle={stickyContainerStyle}
        centeredInParentBackgroundColor={getCenteredInParentBackgroundColor()}
        isBuilderPage={isBuilderPage}
        centeredInParentBackgroundImage={centeredInParentBackgroundImage}
        centeredInParentBackgroundImageSize={
          centeredInParentBackgroundImageSize
        }
        centeredInParentBackgroundImagePositionX={
          centeredInParentBackgroundImagePositionX
        }
        centeredInParentBackgroundImagePositionY={
          centeredInParentBackgroundImagePositionY
        }
        centeredInParentBackgroundImageTablet={
          centeredInParentBackgroundImageTablet
        }
        centeredInParentBackgroundImageMobile={
          centeredInParentBackgroundImageMobile
        }
      >
        {elements?.map((element: any, index: number) => (
          <Fragment key={element?.$unique_key || `root-element-${index}`}>
            {renderElement(
              { ...element, componentId, index },
              {
                path: `elements.${index}`,
              },
              componentId
            )}
          </Fragment>
        ))}
      </Wrapper>
    </DynamicGridContext.Provider>
  );
};
DynamicGrid.getEditingConfig = dynamicGridEditingConfigs;

export default DynamicGrid;
